// Titles: what a player has earned and wears, read off titles/get_user_titles (or player/info's copy) and
// init.titles_config.details (the legacy catalogue: details[name]["0"] = { title, subtitle, text, icon, levels… },
// details[name]["1".."5"] = per-tier data).
import type { UserTitles } from "@/api/types";

export interface TitleView {
  privacy: boolean;
  main: { title: string; tier: number } | null;
  /** earned title keys, main first */
  earned: string[];
  unearned: string[];
  /** earned tiers per title (from titles_earned) */
  tiers: Record<string, number[]>;
  /** per title and tier: how many times it was earned and in which worlds (titles_earned[name][tier]) */
  tierInfo: Record<string, Record<number, { count: number; worlds: number[] }>>;
  earnedCount: number;
  details: Record<string, unknown> | undefined;
}

export function titlesOf(t: Partial<UserTitles> | undefined, details: Record<string, unknown> | undefined): TitleView {
  const privacy = Number(t?.privacy) === 1;
  const mainTitle = t?.title_main?.title ?? null;
  const main = mainTitle ? { title: mainTitle, tier: Number(t?.title_main?.tier) || 1 } : null;
  const tiers: Record<string, number[]> = {};
  const earnedObj = (t?.titles_earned ?? {}) as Record<string, Record<string, unknown>>;
  const tierInfo: TitleView["tierInfo"] = {};
  for (const [name, byTier] of Object.entries(earnedObj)) {
    tiers[name] = Object.keys(byTier ?? {}).map(Number).filter((n) => Number.isFinite(n)).sort((a, b) => a - b);
    tierInfo[name] = {};
    for (const [k, v] of Object.entries(byTier ?? {})) {
      const n = Number(k);
      if (!Number.isFinite(n)) continue;
      const rec = (v ?? {}) as { count?: unknown; earned_in?: unknown };
      const worlds = rec.earned_in && typeof rec.earned_in === "object" ? Object.keys(rec.earned_in as object).map(Number).filter((w) => Number.isFinite(w) && w > 0).sort((a, b) => a - b) : [];
      tierInfo[name][n] = { count: Math.max(1, Number(rec.count) || worlds.length || 1), worlds };
    }
  }
  const earnedSet = new Set<string>([...(t?.earned_title_names ?? []), ...Object.keys(earnedObj)]);
  const earned = [...earnedSet].sort((a, b) => (a === mainTitle ? -1 : b === mainTitle ? 1 : a.localeCompare(b)));
  const unearned = (t?.unearned_title_names ?? []).filter((n) => !earnedSet.has(n));
  return { privacy, main, earned, unearned, tiers, tierInfo, earnedCount: earned.length, details };
}

/** The highest tier earned of a title (the live app shows a title's card at its highest tier — TitlePrefabScript), else null. */
export const topTier = (v: TitleView, key: string): number | null => {
  const t = v.tiers[key] ?? [];
  return t.length ? Math.max(...t) : null;
};

/** The live app's line for one earned tier (TitleInWorldsScript): "Tier 3 · 2× · earned in worlds 4, 7". */
export function tierLine(v: TitleView, key: string, tier: number): string {
  const info = v.tierInfo[key]?.[tier];
  if (!info) return `Tier ${tier}`;
  const n = info.worlds.length;
  return `Tier ${tier}${info.count > 1 ? ` · ${info.count}×` : ""}${n ? ` · earned in world${n === 1 ? "" : "s"} ${info.worlds.join(", ")}` : ""}`;
}

const SMALL = new Set(["of", "the", "and", "a", "an", "in", "on", "to", "for"]);

/** A title's name for people: "titles_valorian_elite_title" → "Valorian Elite", "born_of_fire" → "Born of Fire".
 *  The catalogue's `title` is often a string key (titles_<name>_title) rather than words, so the "titles_" / "_title"
 *  wrapper is dropped, the underscores become spaces and every word is capitalised (small joining words stay lower case
 *  unless they lead). Ben, Oct 10 2026: never show the raw key or an underscore. */
export function prettyTitle(raw: string): string {
  const core = raw.trim().replace(/^titles?_/i, "").replace(/_titles?$/i, "");
  const words = core.split(/[_\s]+/).filter(Boolean);
  return words.map((w, i) => (i > 0 && SMALL.has(w.toLowerCase()) ? w.toLowerCase() : w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())).join(" ");
}

/** A title's display name: the catalogue's title when it is real words, else the key — both through prettyTitle. */
export function titleName(key: string, details: Record<string, unknown> | undefined): string {
  const d = details?.[key] as Record<string, Record<string, unknown>> | undefined;
  const base = d?.["0"] ?? (d as Record<string, unknown> | undefined);
  const t = base?.title;
  if (typeof t === "string" && t.trim()) return /[_]/.test(t) || /^titles?\b/i.test(t.trim()) ? prettyTitle(t) : t.trim();
  return prettyTitle(key);
}

export function titleBlurb(key: string, details: Record<string, unknown> | undefined): string {
  const d = details?.[key] as Record<string, Record<string, unknown>> | undefined;
  const base = d?.["0"];
  // (skip string keys like "titles_valorian_elite_subtitle" — words only)
  for (const k of ["subtitle", "text", "obtain"]) { const v = base?.[k]; if (typeof v === "string" && v.trim() && !/^[a-z0-9]+(_[a-z0-9]+)+$/i.test(v.trim())) return v.trim(); }
  return "";
}

/** The tiers the catalogue knows for a title (1…n), else the earned ones, else [1]. */
export function titleTiers(key: string, details: Record<string, unknown> | undefined, earned: number[] = []): number[] {
  const d = details?.[key] as Record<string, unknown> | undefined;
  const fromCat = d ? Object.keys(d).map(Number).filter((n) => Number.isFinite(n) && n > 0).sort((a, b) => a - b) : [];
  if (fromCat.length) return fromCat;
  if (earned.length) return Array.from({ length: Math.max(...earned) }, (_, i) => i + 1);
  return [1];
}

// ── Pictures (Oct 19 2026). Ben: "The pictures should all be pulled from the back end." The catalogue names each title's
// badge coin per tier (details[name][tier].icon, or details[name]["0"].icon for one-tier titles) and its painted portrait
// (details[name]["0"].image); the files live on the game's host under /images/titles/ and reach the app through the
// Worker (worker/titleArt.ts) — none are bundled.

const fileOf = (v: unknown): string | null => {
  if (typeof v !== "string" || !v.trim()) return null;
  const base = v.trim().split(/[\\/]/).pop() ?? "";
  return /^[A-Za-z0-9_-]{1,120}\.(png|jpe?g|webp)$/.test(base) ? base : null;
};

/** The back end's picture as an app URL (worker/titleArt.ts), or null when the catalogue names none. */
export const titleArtUrl = (file: string | null): string | null => (file ? `/api/title-art/${encodeURIComponent(file)}` : null);

/** A title's badge file for a tier: that tier's icon, else the title's own, else the nearest tier that has one. */
export function titleIconFile(key: string, tier: number | null | undefined, details: Record<string, unknown> | undefined): string | null {
  const d = details?.[key] as Record<string, Record<string, unknown> | undefined> | undefined;
  if (!d) return null;
  const t = Number(tier) || 0;
  const own = (k: string) => fileOf(d[k]?.icon);
  if (t > 0 && own(String(t))) return own(String(t));
  if (own("0")) return own("0");
  const tiers = Object.keys(d).map(Number).filter((n) => Number.isFinite(n) && n > 0 && own(String(n))).sort((a, b) => a - b);
  if (!tiers.length) return null;
  const below = tiers.filter((n) => n <= t);
  return own(String(below.length ? below[below.length - 1] : tiers[0]));
}

/** A title's painted portrait file (the catalogue's `image`), or null. */
export function titleImageFile(key: string, details: Record<string, unknown> | undefined): string | null {
  const d = details?.[key] as Record<string, Record<string, unknown> | undefined> | undefined;
  return fileOf(d?.["0"]?.image);
}
